import type { Comment } from "../api/types";

/**
 * Thread nesting for the review rail. The server returns comments FLAT (roots +
 * replies mixed, ordered by created_at); the client nests them into
 * arbitrary-depth trees by `parent_id`. Pure + deterministic — unit-tested.
 */

export interface ThreadNode {
  comment: Comment;
  replies: ThreadNode[];
}

/** Nest a flat comment list into threads. A comment with `parent_id === null`
 *  (or whose parent is not in the set — a defensive "never lose a comment"
 *  case) becomes a root; every other comment attaches under its parent. Input
 *  order is preserved for both roots and replies (the server orders by time). */
export function buildThreads(comments: Comment[]): ThreadNode[] {
  const nodes = new Map<number, ThreadNode>();
  for (const c of comments) nodes.set(c.id, { comment: c, replies: [] });

  const roots: ThreadNode[] = [];
  for (const c of comments) {
    const node = nodes.get(c.id);
    if (!node) continue;
    const parent = c.parent_id != null ? nodes.get(c.parent_id) : undefined;
    if (parent) parent.replies.push(node);
    else roots.push(node);
  }
  return roots;
}

/** Total number of comments in a thread (the root + every nested reply). */
export function threadSize(node: ThreadNode): number {
  return 1 + node.replies.reduce((n, r) => n + threadSize(r), 0);
}

/** Every comment in a thread, depth-first (root first). */
export function flattenThread(node: ThreadNode): Comment[] {
  return [node.comment, ...node.replies.flatMap(flattenThread)];
}
